import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import SearchBar from '../components/SearchBar';
import ProfileCard from '../components/ProfileCard';
import Footer from '../components/Footer';
import { supabase } from '../config/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight } = FiIcons;

// Helper function to shuffle an array (Used when no results are present)
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const HomePage = () => {
  const [allProfiles, setAllProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const resultsRef = useRef(null);

  useEffect(() => {
    fetchProfiles();
    fetchTags();
  }, []);

  const fetchProfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAllProfiles(data || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const { data, error } = await supabase.from('tags').select('*').order('name');
      if (error) throw error;
      setAllTags(data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const filterProfiles = useCallback(() => {
    let filtered;

    if (!searchQuery && selectedTags.length === 0) {
      // Default behavior: Show a random sampling of all profiles
      filtered = shuffleArray([...allProfiles]);
    } else {
      let searchResults = allProfiles;

      if (selectedTags.length > 0) {
        searchResults = searchResults.filter((profile) =>
          selectedTags.every((selectedTag) =>
            (profile.tags || []).some(
              (profileTag) => profileTag.toLowerCase() === selectedTag.toLowerCase()
            )
          )
        );
      }

      if (searchQuery) {
        searchResults = searchResults.filter(
          (p) =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.instagram_handle.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.tags || []).some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      }
      filtered = searchResults;
    }

    // Limit display results to 25 for performance
    setFilteredProfiles(filtered.slice(0, 25));
  }, [allProfiles, searchQuery, selectedTags]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      filterProfiles();
    }, 300);
    return () => clearTimeout(debounce);
  }, [filterProfiles, searchQuery, selectedTags]);

  const handleTagSelect = useCallback((tag) => {
    setSelectedTags([tag]);
    // On mobile, maybe don't scroll abruptly, or scroll with offset
    if (resultsRef.current) {
      const yOffset = -80; // Adjust for sticky header
      const element = resultsRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  const handleTagRemove = (tag) => {
    setSelectedTags((prevTags) => prevTags.filter((t) => t !== tag));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-12 pb-24 md:pb-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 md:mb-12"
        >
          <h1 className="text-3xl md:text-6xl font-extrabold text-slate-900 mb-3 tracking-tight">
            Find Them Faster
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            The definitive directory of Chaldean businesses, professionals, and creators.
          </p>
        </motion.div>

        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedTags={selectedTags}
          onTagRemove={handleTagRemove}
          allTags={allTags}
          onTagSelect={handleTagSelect}
        />

        <div ref={resultsRef} className="mt-8 md:mt-12">
          {filteredProfiles.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 md:gap-6"
            >
              {filteredProfiles.map((profile) => (
                <ProfileCard key={profile.id} profile={profile} onTagClick={handleTagSelect} />
              ))}
            </motion.div>
          )}

          {filteredProfiles.length === 0 && !loading && (searchQuery || selectedTags.length > 0) && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No profiles found</h3>
              <p className="text-slate-600">Try adjusting your search criteria.</p>
            </motion.div>
          )}
        </div>
      </main>

      {/* Hide Footer on mobile to prevent clutter with bottom nav */}
      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
};

export default HomePage;